import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Package, AlertTriangle, CheckCircle, XCircle,
  TrendingUp, TrendingDown, Euro, Box, Truck, RefreshCw,
  ArrowRight, ExternalLink, Play, Send, Trash2, Loader2, X, Activity,
  ChevronDown, Info, Fuel,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axiosInstance from '../utils/axiosInstance';
import ShippyLoader from '../components/ShippyLoader';

// Country name → flag emoji
const COUNTRY_CODES = {
  'Serbia': 'RS',
  'Belgium': 'BE', 'Netherlands': 'NL', 'France': 'FR', 'Germany': 'DE',
  'Luxembourg': 'LU', 'Spain': 'ES', 'Portugal': 'PT', 'Italy': 'IT',
  'Austria': 'AT', 'Switzerland': 'CH', 'United Kingdom': 'GB',
  'Ireland': 'IE', 'Denmark': 'DK', 'Sweden': 'SE', 'Norway': 'NO',
  'Finland': 'FI', 'Poland': 'PL', 'Czech Republic': 'CZ', 'Czechia': 'CZ',
  'Slovakia': 'SK', 'Hungary': 'HU', 'Romania': 'RO', 'Bulgaria': 'BG',
  'Croatia': 'HR', 'Slovenia': 'SI', 'Greece': 'GR', 'Estonia': 'EE',
  'Latvia': 'LV', 'Lithuania': 'LT', 'Malta': 'MT', 'Cyprus': 'CY',
  'United States': 'US', 'Canada': 'CA', 'Australia': 'AU',
  'New Zealand': 'NZ', 'Japan': 'JP', 'South Korea': 'KR',
  'United Arab Emirates': 'AE', 'Singapore': 'SG', 'Hong Kong': 'HK',
  'Taiwan': 'TW', 'Brazil': 'BR', 'Mexico': 'MX', 'South Africa': 'ZA',
  'Turkey': 'TR', 'Israel': 'IL', 'India': 'IN', 'China': 'CN',
  'Peru': 'PE', 'Argentina': 'AR', 'Chile': 'CL', 'Colombia': 'CO',
  'Monaco': 'MC', 'Iceland': 'IS', 'Kuwait': 'KW', 'Qatar': 'QA',
  'Bahrain': 'BH', 'Jersey': 'JE', 'Armenia': 'AM', 'Malaysia': 'MY',
  'Thailand': 'TH', 'Philippines': 'PH',
  'Liechtenstein': 'LI', 'Ukraine': 'UA', 'Georgia': 'GE', 'Egypt': 'EG', 'Lebanon': 'LB', 'Kazakhstan': 'KZ', 'Seychelles': 'SC', 'Suriname': 'SR', 'Vatican': 'VA', 'San Marino': 'SM', 'Gibraltar': 'GI', 'Andorra': 'AD', 'Albania': 'AL', 'Kosovo': 'XK', 'North Macedonia': 'MK', 'Montenegro': 'ME', 'Bosnia-Herzegovina': 'BA',
};
const countryFlag = (name) => {
  const code = COUNTRY_CODES[name];
  if (!code) return '';
  return String.fromCodePoint(...[...code].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
};

// Countries fully on Shippy carrier service (checkout rates)
const SHIPPY_COUNTRIES = new Set(['BR', 'AU', 'US', 'CA', 'AE', 'JP', 'CN', 'NZ', 'PT', 'ES', 'IT', 'ZA', 'NO', 'PL', 'HK', 'SG', 'SA', 'TW', 'DK', 'SE', 'IE', 'CH', 'FI', 'CZ', 'SK', 'GB', 'GR', 'KR', 'MX', 'AT', 'EE', 'HR', 'HU', 'LT', 'LV', 'SI', 'DE', 'PE', 'LU', 'IL', 'IN', 'MC', 'IS', 'AR', 'KW', 'CO', 'CL', 'QA', 'BH', 'JE', 'AM', 'MY', 'TH', 'PH', 'CY', 'TR', 'RO', 'BG', 'MT', 'LI', 'UA', 'GE', 'EG', 'LB', 'KZ', 'SC', 'SR', 'VA', 'SM', 'GI', 'AD', 'AL', 'XK', 'MK', 'ME', 'BA', 'FR', 'RS', 'BE', 'NL']);
const isShippyCountry = (name) => SHIPPY_COUNTRIES.has(COUNTRY_CODES[name]);
const ShippyBadge = () => (
  <img src="/favicon.svg" alt="Shippy" className="inline-block h-3 w-3 opacity-40 ml-1" title="Shippy carrier service active" />
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [fuel, setFuel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('3'); // '1','3','7','10','30','90','0' (=all) or 'wtd','mtd','ytd'
  const [pushingSlack, setPushingSlack] = useState(null);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [refreshingRates, setRefreshingRates] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);
  const [showRefreshDialog, setShowRefreshDialog] = useState(false);
  const [refreshCountry, setRefreshCountry] = useState('');
  const [expandedCountry, setExpandedCountry] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [useP90, setUseP90] = useState(false);
  const [timelineGran, setTimelineGran] = useState('hourly'); // 'hourly' | 'daily'
  const [timelineHover, setTimelineHover] = useState(null); // hovered bucket index

  // Time-range helpers
  // Numeric strings ('1','3','7','10','30','90') = rolling window in days; '0' = all time;
  // 'wtd' / 'mtd' / 'ytd' = anchored to a calendar date (Mon / 1st of month / Jan 1).
  const TO_DATE_MODES = ['wtd', 'mtd', 'ytd'];

  const computeStartDate = (mode) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    if (mode === 'wtd') {
      const dayIdx = (d.getDay() + 6) % 7; // Mon=0 ... Sun=6
      d.setDate(d.getDate() - dayIdx);
    } else if (mode === 'mtd') {
      d.setDate(1);
    } else if (mode === 'ytd') {
      d.setMonth(0, 1);
    }
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const buildRangeParams = (mode) => {
    if (TO_DATE_MODES.includes(mode)) return { start_date: computeStartDate(mode) };
    const n = Number(mode);
    return n > 0 ? { days: n } : {};
  };

  const formatRangeLabel = (mode) => {
    if (mode === 'wtd') return 'week to date';
    if (mode === 'mtd') return 'month to date';
    if (mode === 'ytd') return 'year to date';
    const n = Number(mode);
    if (n === 1) return 'last 24h';
    if (n > 0) return `last ${n} days`;
    return 'all time';
  };

  // For places that need a payload `days` value (Refresh dialog).
  // Anchored modes don't have a clean integer; default to 7 for those.
  const refreshDaysForMode = (mode) => {
    if (TO_DATE_MODES.includes(mode)) {
      const start = new Date(computeStartDate(mode));
      const now = new Date(); now.setHours(0, 0, 0, 0);
      return Math.max(1, Math.ceil((now - start) / 86400000) + 1);
    }
    const n = Number(mode);
    return n > 0 ? n : 3;
  };

  const handlePushToSlack = async (alert) => {
    setPushingSlack(alert.id);
    try {
      const response = await axiosInstance.post('api/analyze-packing/', {
        order_number: alert.order_number,
        force_slack: true,
      });
      if (response.data.slack_sent) {
        setStats(prev => ({
          ...prev,
          latest_alerts: prev.latest_alerts.map(a =>
            a.id === alert.id ? { ...a, slack_sent: true } : a
          ),
        }));
      } else {
        window.alert('Slack notification could not be sent.');
      }
    } catch (err) {
      window.alert(err.response?.data?.message || 'Failed to push to Slack');
    } finally {
      setPushingSlack(null);
    }
  };

  const handleRefreshRates = async () => {
    setRefreshingRates(true);
    setRefreshResult(null);
    try {
      const payload = { days: refreshDaysForMode(timeRange) };
      if (refreshCountry) payload.country = refreshCountry;
      const response = await axiosInstance.post('api/analyze-packing/refresh/', payload);
      setRefreshResult(response.data.summary);
      setShowRefreshDialog(false);
      setRefreshCountry('');
      fetchStats();
    } catch (err) {
      setRefreshResult({ error: err.response?.data?.message || 'Refresh failed' });
    } finally {
      setRefreshingRates(false);
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Delete this analysis record?')) return;
    setDeletingRecord(recordId);
    try {
      const response = await axiosInstance.delete(`api/packing-analysis-records/${recordId}/`);
      if (response.data.status === 'success') {
        setStats(prev => ({
          ...prev,
          latest_alerts: prev.latest_alerts.filter(a => a.id !== recordId),
        }));
      }
    } catch (err) {
      window.alert(err.response?.data?.message || 'Failed to delete');
    } finally {
      setDeletingRecord(null);
    }
  };

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildRangeParams(timeRange);
      const response = await axiosInstance.get('api/dashboard/stats/', { params });
      if (response.data.status === 'success') {
        setStats(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    axiosInstance.get('api/fuel-index/status/')
      .then(r => setFuel(r.data?.sources || []))
      .catch(() => setFuel([]));
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <ShippyLoader size={72} message="Loading dashboard..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-8">
        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 p-6 text-center">
          <p className="text-red-800 dark:text-red-200">{error}</p>
          <Button variant="outline" className="mt-3" onClick={fetchStats}>
            <RefreshCw className="h-4 w-4 mr-2" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const { overview, top_boxes, total_bin_uses = 0, unused_boxes = [], unused_pallets = [], country_stats, latest_alerts, latest_errors = [], simulation_volume = {} } = stats;

  // Helpers
  const fmt = (v) => {
    if (v == null) return '--';
    return Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const fmtPct = (v) => v != null ? `${v.toFixed(1)}%` : '--';

  // Bar chart helper — simple horizontal bar
  const maxBoxCount = top_boxes.length > 0 ? top_boxes[0].count : 1;

  return (
    <div className="px-3 py-4 md:px-6 md:py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-foreground">
            <LayoutDashboard className="h-7 w-7 text-primary" />
            Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Overview of packing analyses across {overview.total_records.toLocaleString()} orders{timeRange !== '0' ? ` (${formatRangeLabel(timeRange)})` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <optgroup label="Rolling">
              <option value="1">Last 24h</option>
              <option value="3">Last 3 days</option>
              <option value="7">Last 7 days</option>
              <option value="10">Last 10 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </optgroup>
            <optgroup label="To date">
              <option value="wtd">Week to date (Mon→now)</option>
              <option value="mtd">Month to date</option>
              <option value="ytd">Year to date</option>
            </optgroup>
            <optgroup label="Other">
              <option value="0">All time</option>
            </optgroup>
          </select>
          <Button variant="outline" onClick={fetchStats}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button variant="outline" onClick={() => setShowRefreshDialog(true)}>
            <RefreshCw className="h-4 w-4 mr-2" /> Recalculate Rates
          </Button>
        </div>
      </div>

      {/* Refresh dialog */}
      {showRefreshDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => !refreshingRates && setShowRefreshDialog(false)}>
          <div className="bg-card border border-border rounded-xl shadow-lg w-full max-w-md mx-4 animate-in fade-in zoom-in-95" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-primary" />
                Recalculate Shipping Costs
              </h3>
              <button onClick={() => !refreshingRates && setShowRefreshDialog(false)} className="rounded-md p-1 hover:bg-muted text-muted-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-muted-foreground">
                Re-run packing analysis for recent orders using the latest shipping rates. Old records will be replaced. No Slack alerts will be sent.
              </p>
              <div>
                <label className="text-sm font-medium text-foreground block mb-1.5">Country (optional)</label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={refreshCountry}
                  onChange={e => setRefreshCountry(e.target.value)}
                >
                  <option value="">All countries</option>
                  {(stats?.country_stats || []).map(cs => (
                    <option key={cs.shipping_country} value={cs.shipping_country}>
                      {countryFlag(cs.shipping_country)} {cs.shipping_country} ({cs.count} orders)
                    </option>
                  ))}
                </select>
              </div>
              <div className="text-xs text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
                Time range: <span className="font-medium text-foreground">{formatRangeLabel(timeRange)}</span>
                {' '}&mdash; matches the dashboard filter above
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-5 border-t border-border">
              <Button variant="outline" onClick={() => setShowRefreshDialog(false)} disabled={refreshingRates}>
                Cancel
              </Button>
              <Button onClick={handleRefreshRates} disabled={refreshingRates}>
                {refreshingRates ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                {refreshingRates ? 'Recalculating...' : 'Recalculate'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Refresh result banner */}
      {refreshResult && !refreshResult.error && (
        <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/20 p-3 flex items-center justify-between">
          <p className="text-sm text-emerald-800 dark:text-emerald-200">
            Recalculated {refreshResult.success} of {refreshResult.found} orders
            {refreshResult.country_filter && <span className="ml-1">({refreshResult.country_filter})</span>}
            {refreshResult.errors > 0 && <span className="text-amber-600 ml-1"> &middot; {refreshResult.errors} errors</span>}
          </p>
          <button onClick={() => setRefreshResult(null)} className="text-emerald-600 hover:text-emerald-800 text-sm">&times;</button>
        </div>
      )}
      {refreshResult?.error && (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-3 flex items-center justify-between">
          <p className="text-sm text-red-800 dark:text-red-200">{refreshResult.error}</p>
          <button onClick={() => setRefreshResult(null)} className="text-red-600 hover:text-red-800 text-sm">&times;</button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="rounded-lg border border-border/70 surface-panel p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="h-4 w-4 text-emerald-600" />
            <p className="text-sm text-muted-foreground">Orders Analysed</p>
          </div>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-semibold text-foreground">{overview.total_records.toLocaleString()}</p>
            {overview.total_records > 0 && (
              <span className="text-sm font-semibold text-emerald-600">
                {(((overview.total_success + overview.total_alerts) / overview.total_records) * 100).toFixed(1)}% successful
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {overview.total_success.toLocaleString()} OK
            {overview.total_alerts > 0 && <span className="text-amber-500"> + {overview.total_alerts} with alerts</span>}
            {overview.total_errors > 0 && (
              <button
                onClick={() => setShowErrors((v) => !v)}
                className="ml-1 text-red-400 hover:text-red-600 hover:underline cursor-pointer inline-flex items-center gap-0.5"
              >
                &middot; {overview.total_errors} error{overview.total_errors !== 1 ? 's' : ''}
                <ChevronDown className={`h-3 w-3 transition-transform ${showErrors ? 'rotate-180' : ''}`} />
              </button>
            )}
          </p>
          {showErrors && latest_errors.length > 0 && (
            <div className="mt-2 rounded-md border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20 overflow-hidden">
              {latest_errors.map((err) => (
                <div key={err.id} className="px-3 py-1.5 text-xs border-b border-red-200/50 dark:border-red-800/50 last:border-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-red-700 dark:text-red-300">#{err.order_number}</span>
                    {err.shipping_country && (
                      <span className="text-muted-foreground">{err.shipping_country}</span>
                    )}
                  </div>
                  {err.error_message && (
                    <p className="text-red-600/70 dark:text-red-400/70 mt-0.5 truncate">{err.error_message}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-lg border border-border/70 surface-panel p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <p className="text-sm text-muted-foreground">Alerts</p>
          </div>
          <p className="text-2xl font-semibold text-amber-600">{overview.total_alerts.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground mt-0.5">shipping cost variance or margin danger</p>
        </div>
        <div className="rounded-lg border border-border/70 surface-panel p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center gap-2 mb-1">
            <Euro className="h-4 w-4 text-primary" />
            <p className="text-sm text-muted-foreground">Avg Charged vs Simulated</p>
          </div>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-semibold text-foreground">{fmt(useP90 && overview.avg_charged_p90 ? overview.avg_charged_p90 : overview.avg_charged)}</p>
            <p className="text-sm text-muted-foreground">vs</p>
            <p className="text-lg font-semibold text-emerald-600">{fmt(useP90 && overview.avg_optimal_p90 ? overview.avg_optimal_p90 : overview.avg_optimal)}</p>
          </div>
          {(() => {
            const savings = useP90 && overview.avg_savings_p90 != null ? overview.avg_savings_p90 : overview.avg_savings;
            return (
              <p className="text-xs mt-0.5">
                {savings > 0 ? (
                  <span className="text-emerald-600">Avg overcharge: +{fmt(savings)}</span>
                ) : (
                  <span className="text-red-600">Avg undercharge: {fmt(savings)}</span>
                )}
                {useP90 && <span className="text-muted-foreground ml-2">(P90)</span>}
              </p>
            );
          })()}
        </div>
        <div className="rounded-lg border border-border/70 surface-panel p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            <p className="text-sm text-muted-foreground">Franco Loss %</p>
          </div>
          {(() => {
            const francoLossPct = overview.total_order_value > 0
              ? ((overview.total_optimal || 0) - (overview.total_charged || 0)) / overview.total_order_value * 100
              : null;
            const colorClass = francoLossPct === null
              ? 'text-foreground'
              : francoLossPct < 5 ? 'text-emerald-600' : francoLossPct < 10 ? 'text-amber-600' : 'text-red-600';
            return (
              <>
                <p className={`text-2xl font-semibold ${colorClass}`}>
                  {francoLossPct === null ? '--' : `${francoLossPct.toFixed(1)}%`}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  (Shippy cost − shipping charged) / revenue. Target &lt;5%.
                </p>
              </>
            );
          })()}
        </div>
        <div className="rounded-lg border border-border/70 surface-panel p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="h-4 w-4 text-emerald-500" />
            <p className="text-sm text-muted-foreground">Simulations Today</p>
          </div>
          <p className="text-2xl font-semibold text-foreground">{(simulation_volume.today || 0).toLocaleString()}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            <span className="text-emerald-500">{simulation_volume.today_shopify || 0} Shopify</span>
            {' + '}
            <span className="text-violet-500">{simulation_volume.today_b2b || 0} B2B</span>
            {(simulation_volume.today_checkout || 0) > 0 && <>
              {' + '}
              <span className="text-amber-500">{simulation_volume.today_checkout} Checkout</span>
            </>}
            <span className="ml-1.5 text-muted-foreground/70">({simulation_volume.avg_per_day || 0}/day avg)</span>
          </p>
        </div>
      </div>

      {/* Two-column: left (hourly + boxes) | right (countries) */}
      <div className="grid gap-4 lg:grid-cols-2 lg:auto-rows-fr">
        {/* Left column: hourly chart + boxes stacked */}
        <div className="flex flex-col gap-3">
          {/* Simulation Volume — real timeline (last 7 days) + prior-week benchmark */}
          {simulation_volume.timeline && (() => {
            const tl = simulation_volume.timeline;
            const gran = timelineGran;
            const cur = gran === 'hourly' ? tl.hourly.current : tl.daily.current;
            const prev = gran === 'hourly' ? tl.hourly.previous : tl.daily.previous;
            const n = cur.length;
            if (!n) return null;
            const W = 480, H = 96;
            const maxY = Math.max(1, ...cur.map(d => d.total), ...prev.map(d => d.total));
            const X = (i) => n <= 1 ? W / 2 : (i / (n - 1)) * W;
            const Y = (v) => H - (v / maxY) * H;

            // Stacked cumulative tops: Shopify (bottom) → +B2B → +Checkout (=total)
            const sTop = cur.map(d => d.shopify);
            const bTop = cur.map(d => d.shopify + d.b2b);
            const cTop = cur.map(d => d.total);
            const zero = cur.map(() => 0);
            const band = (top, bot) => {
              let d = '';
              for (let i = 0; i < n; i++) d += `${i ? ' L' : 'M'} ${X(i).toFixed(1)} ${Y(top[i]).toFixed(1)}`;
              for (let i = n - 1; i >= 0; i--) d += ` L ${X(i).toFixed(1)} ${Y(bot[i]).toFixed(1)}`;
              return d + ' Z';
            };
            const linePath = (vals) => vals.map((v, i) => `${i ? 'L' : 'M'} ${X(i).toFixed(1)} ${Y(v).toFixed(1)}`).join(' ');
            const benchPath = linePath(prev.map(d => d.total));

            // X-axis ticks: day boundaries (hourly) or each day (daily)
            const ticks = [];
            if (gran === 'hourly') {
              cur.forEach((d, i) => {
                const hh = d.t.slice(11, 13);
                if (hh === '00' || i === 0) {
                  const [y, m, dd] = d.t.slice(0, 10).split('-').map(Number);
                  const dow = new Date(Date.UTC(y, m - 1, dd)).toLocaleDateString('en-US', { weekday: 'short' });
                  ticks.push({ i, label: `${dow} ${dd}`, boundary: hh === '00' });
                }
              });
            } else {
              cur.forEach((d, i) => {
                const dd = Number(d.date.slice(8, 10));
                ticks.push({ i, label: `${d.dow} ${dd}`, boundary: false });
              });
            }

            const curTotal = tl.current_total, prevTotal = tl.previous_total;
            const deltaPct = prevTotal > 0 ? Math.round(((curTotal - prevTotal) / prevTotal) * 100) : 0;
            const fmtK = (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`;

            const hov = (timelineHover != null && timelineHover >= 0 && timelineHover < n) ? timelineHover : null;
            const hd = hov != null ? cur[hov] : null;
            const hp = hov != null ? prev[hov] : null;
            const hovLabel = (() => {
              if (hd == null) return '';
              if (gran === 'hourly') {
                const [y, m, dd] = hd.t.slice(0, 10).split('-').map(Number);
                const dow = new Date(Date.UTC(y, m - 1, dd)).toLocaleDateString('en-US', { weekday: 'short' });
                return `${dow} ${dd} · ${hd.t.slice(11, 16)}`;
              }
              const dd = Number(hd.date.slice(8, 10));
              const mo = new Date(Date.UTC(Number(hd.date.slice(0, 4)), Number(hd.date.slice(5, 7)) - 1, dd)).toLocaleDateString('en-US', { month: 'short' });
              return `${hd.dow} ${dd} ${mo}`;
            })();

            const onMove = (e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const frac = (e.clientX - rect.left) / rect.width;
              setTimelineHover(Math.max(0, Math.min(n - 1, Math.round(frac * (n - 1)))));
            };

            return (
              <div className="rounded-xl border border-border/70 surface-panel shadow-sm">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-border gap-2 flex-wrap">
                  <h2 className="text-sm font-semibold flex items-center gap-2">
                    <Activity className="h-4 w-4 text-emerald-500" /> Simulation Volume
                    <span className="text-[11px] font-normal text-muted-foreground">last 7 days</span>
                  </h2>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-400" /> Shopify</span>
                      <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-violet-400" /> B2B</span>
                      <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-400" /> Checkout</span>
                      <span className="flex items-center gap-1"><span className="inline-block w-3 h-0 border-t border-dashed border-muted-foreground/70" /> Prev wk</span>
                    </div>
                    <div className="flex rounded-md border border-border overflow-hidden text-[11px]">
                      {['hourly', 'daily'].map(g => (
                        <button key={g} onClick={() => { setTimelineGran(g); setTimelineHover(null); }}
                          className={`px-2 py-0.5 capitalize transition-colors ${gran === g ? 'bg-primary/10 text-primary font-semibold' : 'text-muted-foreground hover:bg-muted'}`}>
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Headline: this week vs prior week */}
                <div className="px-4 pt-2 flex items-baseline gap-2">
                  <span className="text-lg font-bold tabular-nums">{curTotal.toLocaleString()}</span>
                  <span className="text-[11px] text-muted-foreground">sims this week</span>
                  <span className={`text-[11px] font-semibold tabular-nums ${deltaPct > 0 ? 'text-emerald-500' : deltaPct < 0 ? 'text-rose-500' : 'text-muted-foreground'}`}>
                    {deltaPct > 0 ? '▲' : deltaPct < 0 ? '▼' : ''} {deltaPct > 0 ? '+' : ''}{deltaPct}% vs prior week ({prevTotal.toLocaleString()})
                  </span>
                </div>

                <div className="px-4 pb-2 pt-1 relative">
                  <svg viewBox={`0 0 ${W} ${H + 16}`} className="w-full overflow-visible" style={{ maxHeight: 140 }}
                    onMouseMove={onMove} onMouseLeave={() => setTimelineHover(null)}>
                    {/* Grid */}
                    {[0.25, 0.5, 0.75, 1].map(frac => (
                      <line key={frac} x1="0" x2={W} y1={H - H * frac} y2={H - H * frac}
                        stroke="currentColor" strokeOpacity="0.07" strokeWidth="1" />
                    ))}
                    {/* Day-boundary verticals (hourly) */}
                    {gran === 'hourly' && ticks.filter(t => t.boundary).map(t => (
                      <line key={`b${t.i}`} x1={X(t.i)} x2={X(t.i)} y1="0" y2={H}
                        stroke="currentColor" strokeOpacity="0.08" strokeWidth="1" />
                    ))}
                    {/* Stacked source areas (current week) */}
                    <path d={band(sTop, zero)} fill="#34d399" opacity="0.85" />
                    <path d={band(bTop, sTop)} fill="#a78bfa" opacity="0.85" />
                    <path d={band(cTop, bTop)} fill="#fbbf24" opacity="0.85" />
                    {/* Prior-week benchmark line */}
                    <path d={benchPath} fill="none" stroke="currentColor" strokeOpacity="0.55"
                      strokeWidth="1.5" strokeDasharray="4 3" className="text-muted-foreground" />
                    {/* Hover guide + markers */}
                    {hov != null && (
                      <g>
                        <line x1={X(hov)} x2={X(hov)} y1="0" y2={H} stroke="currentColor" strokeOpacity="0.35" strokeWidth="1" />
                        <circle cx={X(hov)} cy={Y(cTop[hov])} r="2.5" fill="#0f172a" stroke="#fff" strokeWidth="1" className="dark:fill-white dark:stroke-slate-900" />
                        {hp && <circle cx={X(hov)} cy={Y(hp.total)} r="2" fill="none" stroke="currentColor" strokeOpacity="0.7" strokeWidth="1.5" className="text-muted-foreground" />}
                      </g>
                    )}
                    {/* Y-axis labels */}
                    <text x={W - 2} y="9" textAnchor="end" className="fill-muted-foreground" fontSize="9">{maxY}</text>
                    <text x={W - 2} y={H / 2 + 3} textAnchor="end" className="fill-muted-foreground" fontSize="9">{Math.round(maxY / 2)}</text>
                    {/* X-axis labels */}
                    {ticks.map(t => (
                      <text key={`l${t.i}`} x={X(t.i)} y={H + 12}
                        textAnchor={t.i === 0 ? 'start' : t.i === n - 1 ? 'end' : 'middle'}
                        className="fill-muted-foreground" fontSize="8.5">{t.label}</text>
                    ))}
                  </svg>

                  {/* Tooltip */}
                  {hov != null && hd && (
                    <div className="pointer-events-none absolute z-10 -translate-x-1/2 rounded-lg border border-border bg-popover text-popover-foreground shadow-md px-2.5 py-1.5 text-[11px] whitespace-nowrap"
                      style={{ left: `calc(1rem + (100% - 2rem) * ${(hov / Math.max(n - 1, 1)).toFixed(4)})`, top: 4 }}>
                      <div className="font-semibold mb-0.5">{hovLabel}</div>
                      <div className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-sm bg-emerald-400" />Shopify<span className="ml-auto font-mono tabular-nums pl-2">{hd.shopify}</span></div>
                      <div className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-sm bg-violet-400" />B2B<span className="ml-auto font-mono tabular-nums pl-2">{hd.b2b}</span></div>
                      <div className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-sm bg-amber-400" />Checkout<span className="ml-auto font-mono tabular-nums pl-2">{hd.checkout}</span></div>
                      <div className="flex items-center gap-1.5 border-t border-border mt-0.5 pt-0.5 font-semibold">Total<span className="ml-auto font-mono tabular-nums pl-2">{hd.total}</span></div>
                      {hp && <div className="flex items-center gap-1.5 text-muted-foreground">Prev wk<span className="ml-auto font-mono tabular-nums pl-2">{hp.total}{hp.total > 0 ? ` (${hd.total - hp.total >= 0 ? '+' : ''}${hd.total - hp.total})` : ''}</span></div>}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Most Used Boxes */}
          <div className="rounded-xl border border-border/70 surface-panel shadow-sm flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0">
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <Box className="h-4 w-4 text-primary" /> Most Used Boxes
              </h2>
              <span className="text-xs text-muted-foreground">{(total_bin_uses || top_boxes.reduce((s, b) => s + b.count, 0)).toLocaleString()} bin uses</span>
            </div>
            <div className="px-4 py-2 space-y-1.5 overflow-y-auto flex-1">
              {top_boxes.map((box) => (
                <div key={box.name} className="flex items-center gap-2">
                  <span className="text-[11px] font-mono w-24 shrink-0 truncate" title={box.name}>{box.name}</span>
                  <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary/70 rounded-full transition-all"
                      style={{ width: `${(box.count / maxBoxCount) * 100}%` }}
                    />
                  </div>
                  <span className="text-[11px] font-semibold w-10 text-right">{box.count.toLocaleString()}</span>
                </div>
              ))}
              {top_boxes.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No box data yet</p>
              )}
            </div>
            {/* Unused Boxes */}
            {unused_boxes.length > 0 && (
              <div className="border-t border-border px-4 py-2 shrink-0">
                <p className="text-xs font-semibold text-amber-600 flex items-center gap-1.5 mb-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {unused_boxes.length} box{unused_boxes.length !== 1 ? 'es' : ''} never used
                </p>
                <div className="flex flex-wrap gap-1">
                  {unused_boxes.map((box) => (
                    <span
                      key={box.name}
                      className="inline-flex items-center rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 px-1.5 py-0.5 text-[11px] font-mono text-amber-700 dark:text-amber-400"
                      title={`${box.dimensions} cm - packaging cost: ${fmt(box.packaging_cost)}`}
                    >
                      {box.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* Unused Pallets */}
            {unused_pallets.length > 0 && (
              <div className="border-t border-border px-4 py-2 shrink-0">
                <p className="text-xs font-semibold text-slate-500 flex items-center gap-1.5 mb-1.5">
                  <Truck className="h-3.5 w-3.5" />
                  {unused_pallets.length} pallet{unused_pallets.length !== 1 ? 's' : ''} never used
                </p>
                <div className="flex flex-wrap gap-1">
                  {unused_pallets.map((p) => (
                    <span
                      key={p.name}
                      className="inline-flex items-center rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/20 px-1.5 py-0.5 text-[11px] font-mono text-slate-600 dark:text-slate-400"
                      title={`${p.dimensions} cm - packaging cost: ${fmt(p.packaging_cost)}`}
                    >
                      {p.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Country Stats (right column) */}
        <div className="rounded-xl border border-border/70 surface-panel shadow-sm">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Truck className="h-4 w-4 text-primary" /> Shipping by Country
              <button
                onClick={() => setUseP90(v => !v)}
                className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors ${
                  useP90
                    ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {useP90 ? 'P90' : 'All'}
              </button>
            </h2>
            <button
              className="text-xs text-primary hover:underline flex items-center gap-1"
              onClick={() => navigate('/shipping-rates')}
            >
              View rates <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-3 py-2 text-left font-medium">Country</th>
                  <th className="px-2 py-2 text-right font-medium">Orders</th>
                  <th className="px-2 py-2 text-right font-medium whitespace-nowrap">Avg Chrg</th>
                  <th className="px-2 py-2 text-right font-medium whitespace-nowrap">Avg Sim</th>
                  <th className="px-2 py-2 text-right font-medium">Delta</th>
                  <th className="px-2 py-2 text-right font-medium whitespace-nowrap">Total P/L</th>
                  <th className="px-2 py-2 text-right font-medium whitespace-nowrap">Free Ship</th>
                  <th className="px-2 py-2 text-right font-medium whitespace-nowrap" title="Franco Loss: (Shippy cost − shipping charged) / product revenue. Target <5%.">Fr. L</th>
                  <th className="px-2 py-2 text-right font-medium whitespace-nowrap">
                    <span className="inline-flex items-center justify-end gap-1">
                      Avg Margin %
                      <span
                        className="cursor-help text-muted-foreground/70 hover:text-muted-foreground"
                        title={
                          'Net margin per order, averaged across the country.\n\n' +
                          'Formula: (Order Value − Order Cost − Commission + Shipping Charged − Shipping Estimated) / Order Value\n\n' +
                          'Already includes COGS, professional commission, and shipping P/L.\n' +
                          'Color: ≥25% green · 20–25% amber · <20% red.'
                        }
                      >
                        <Info className="h-3 w-3" />
                      </span>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const fmt0 = (v) => v == null ? '--' : Math.round(v).toLocaleString();
                  const fmt1 = (v) => v == null ? '--' : v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                  return country_stats.map((cs) => {
                  const charged = useP90 && cs.avg_charged_p90 > 0 ? cs.avg_charged_p90 : cs.avg_charged;
                  const optimal = useP90 && cs.avg_optimal_p90 > 0 ? cs.avg_optimal_p90 : cs.avg_optimal;
                  const delta = charged - optimal;
                  const totalPL = useP90
                    ? delta * cs.count
                    : (cs.total_charged || 0) - (cs.total_optimal || 0);
                  const francoLossPct = cs.total_order_value > 0
                    ? ((cs.total_optimal || 0) - (cs.total_charged || 0)) / cs.total_order_value * 100
                    : null;
                  return (
                    <React.Fragment key={cs.shipping_country}>
                      <tr
                        className="border-b border-border hover:bg-muted/30 cursor-pointer"
                        onClick={() => setExpandedCountry(cs.shipping_country)}
                        title="Click for carrier breakdown"
                      >
                        <td className="px-3 py-1.5 font-medium whitespace-nowrap">
                          <span className="inline-flex items-center gap-1">
                            {countryFlag(cs.shipping_country)} {cs.shipping_country}
                            {isShippyCountry(cs.shipping_country) && <ShippyBadge />}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-right">{cs.count.toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right font-mono">
                          {fmt1(charged)}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono">
                          {fmt0(optimal)}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono">
                          <span className={delta >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                            {delta >= 0 ? '+' : ''}{fmt0(delta)}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono font-semibold">
                          <span className={totalPL >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                            {totalPL >= 0 ? '+' : ''}{fmt0(totalPL)}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono whitespace-nowrap">
                          {cs.zero_charged_count > 0 ? (
                            <span className="text-amber-600" title={`${cs.zero_charged_count} of ${cs.count} orders charged €0`}>
                              {cs.zero_charged_count} <span className="text-muted-foreground">({((cs.zero_charged_count / cs.count) * 100).toFixed(0)}%)</span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono whitespace-nowrap">
                          {francoLossPct === null ? (
                            <span className="text-muted-foreground">--</span>
                          ) : francoLossPct < 5 ? (
                            <span className="text-emerald-600">
                              {francoLossPct.toFixed(1)}%
                            </span>
                          ) : (
                            <span
                              className={
                                francoLossPct < 10
                                  ? 'inline-flex items-center rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ring-amber-500/30'
                                  : 'inline-flex items-center rounded-full bg-red-500/15 text-red-700 dark:text-red-400 px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ring-red-500/40'
                              }
                              title={`Franco loss ${francoLossPct.toFixed(1)}% is above the 5% target`}
                            >
                              ⚠ {francoLossPct.toFixed(1)}%
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {cs.avg_margin_pct == null ? (
                            <span className="text-muted-foreground">--</span>
                          ) : cs.avg_margin_pct >= 25 ? (
                            <span className="text-emerald-600">{Math.round(cs.avg_margin_pct)}%</span>
                          ) : cs.avg_margin_pct >= 20 ? (
                            <span
                              className="inline-flex items-center rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ring-amber-500/30"
                              title={`Avg margin ${Math.round(cs.avg_margin_pct)}% is below the 25% healthy threshold`}
                            >
                              ⚠ {Math.round(cs.avg_margin_pct)}%
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center rounded-full bg-red-500/15 text-red-700 dark:text-red-400 px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ring-red-500/40"
                              title={`Avg margin ${Math.round(cs.avg_margin_pct)}% is below the 20% red-line`}
                            >
                              ⚠ {Math.round(cs.avg_margin_pct)}%
                            </span>
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                });
                })()}
                {country_stats.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">No data yet</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Fuel index status: what the carriers publish vs what our lanes carry */}
      {fuel && fuel.length > 0 && (
        <div className="rounded-xl border border-border/70 surface-panel shadow-sm">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Fuel className="h-4 w-4 text-orange-500" /> Fuel index
              {(() => { const off = fuel.filter(f => f.current && f.lanes > 0 && !f.drift.on_index).length; return off > 0
                ? <span className="ml-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 text-[10px] font-medium">{off} off the index</span>
                : <span className="ml-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 text-[10px] font-medium">lanes on the index</span>; })()}
              {fuel.some(f => f.is_active && f.last_error) && (
                <span className="ml-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 text-[10px] font-medium">a page could not be read</span>
              )}
            </h2>
            <button className="text-xs text-primary hover:underline flex items-center gap-1" onClick={() => navigate('/fuel-index')}>
              Open the Fuel Index page <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          <div className="grid gap-px bg-border/60 sm:grid-cols-2 lg:grid-cols-4">
            {fuel.map(f => {
              const pct = (p) => (p === null || p === undefined) ? '–' : `${(p * 100).toFixed(2)}%`;
              const vals = Object.entries(f.values || {}).map(([v, n]) => `${n}×${(parseFloat(v) * 100).toFixed(1)}%`).join(', ');
              const gap = f.drift?.max_gap_pts || 0;
              const tone = !f.is_active ? 'text-muted-foreground'
                : f.last_error ? 'text-amber-700 dark:text-amber-300'
                : (f.lanes === 0 || f.drift.on_index) ? 'text-emerald-700 dark:text-emerald-300'
                : 'text-red-700 dark:text-red-300';
              return (
                <button key={f.key} onClick={() => navigate('/fuel-index')} className="bg-card text-left p-3 hover:bg-muted/40 transition-colors">
                  <div className="text-[11px] text-muted-foreground truncate" title={f.name}>{f.name}</div>
                  <div className="flex items-baseline gap-2 mt-0.5">
                    <span className="text-lg font-semibold tabular-nums">{f.is_active ? pct(f.current?.pct) : 'manual'}</span>
                    {f.is_active && f.target_pct !== null && f.target_pct !== undefined && Math.abs(f.target_pct - (f.current?.pct ?? 0)) > 0.00001 && <span className="text-[10px] text-muted-foreground">= {pct(f.target_pct)} on lanes</span>}
                    {f.next && <span className="text-[10px] text-orange-600">→ {pct(f.next.pct)}</span>}
                  </div>
                  <div className={`text-[11px] ${tone} truncate`} title={f.last_error || vals}>
                    {!f.is_active ? `lanes ${vals || '–'}`
                      : f.last_error ? 'page could not be read'
                      : f.lanes === 0 ? 'information only'
                      : f.drift.on_index ? `${f.lanes} lanes on the index`
                      : `${f.drift.lanes_off} lanes ${gap > 0 ? 'under' : 'over'} by ${Math.abs(gap).toFixed(1)} pt (${vals})`}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Latest Alerts */}
      <div className="rounded-xl border border-border/70 surface-panel shadow-sm">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> Latest Alerts
          </h2>
          <button
            className="text-xs text-primary hover:underline flex items-center gap-1"
            onClick={() => navigate('/packing-simulator')}
          >
            View history <ArrowRight className="h-3 w-3" />
          </button>
        </div>
        {latest_alerts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-3 py-2 text-left font-medium">Order</th>
                  <th className="px-3 py-2 text-left font-medium">Country</th>
                  <th className="px-3 py-2 text-right font-medium">Charged</th>
                  <th className="px-3 py-2 text-right font-medium">Simulated</th>
                  <th className="px-3 py-2 text-right font-medium">Diff</th>
                  <th className="px-3 py-2 text-right font-medium">Variance</th>
                  <th className="px-3 py-2 text-right font-medium">Margin %</th>
                  <th className="px-3 py-2 text-left font-medium">Solution</th>
                  <th className="px-3 py-2 text-left font-medium">Date</th>
                  <th className="px-3 py-2 text-center font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {latest_alerts.map((alert) => (
                  <tr key={alert.id} className="border-b border-border hover:bg-muted/30">
                    <td className="px-3 py-1.5">
                      <button
                        className="font-mono text-primary hover:underline"
                        onClick={() => navigate(`/packing-simulator?order=${alert.order_number.replace('#', '')}`)}
                      >
                        {alert.order_number}
                      </button>
                    </td>
                    <td className="px-3 py-1.5">
                      {countryFlag(alert.shipping_country)} {alert.shipping_country}
                      {isShippyCountry(alert.shipping_country) && <ShippyBadge />}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmt(alert.amount_charged_shopify)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmt(alert.amount_calculated_optimal)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">
                      <span className={alert.cost_difference > 0 ? 'text-emerald-600' : 'text-red-600'}>
                        {alert.cost_difference > 0 ? '+' : ''}{fmt(alert.cost_difference)}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <span className={Math.abs(alert.variance_percent) > 30 ? 'text-red-600 font-semibold' : 'text-amber-600'}>
                        {fmtPct(alert.variance_percent)}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <span className={alert.gross_margin_percent >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                        {fmtPct(alert.gross_margin_percent)}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 max-w-[200px] truncate" title={alert.shipping_solution}>
                      {alert.shipping_method === 'pickup' && (
                        <span className="inline-flex items-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300 px-1.5 py-0.5 text-[9px] font-medium mr-1">Pickup</span>
                      )}
                      {alert.shipping_method === 'split_shipment' && (
                        <span className="inline-flex items-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-1.5 py-0.5 text-[9px] font-medium mr-1">Split</span>
                      )}
                      {alert.shipping_method === 'forced_carrier' && (
                        <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 text-[9px] font-medium mr-1" title="Customer picked this carrier at checkout — cost recorded is that carrier's, not the cheapest">Forced carrier</span>
                      )}
                      {alert.shipping_solution || '-'}
                    </td>
                    <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">
                      {new Date(alert.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      <div className="flex items-center justify-center gap-0.5">
                        <button
                          className="rounded p-1 hover:bg-primary/10 text-primary"
                          onClick={() => navigate(`/packing-simulator?order=${alert.order_number.replace('#', '')}`)}
                          title="Simulate this order"
                        >
                          <Play className="h-3.5 w-3.5" />
                        </button>
                        {alert.order_id && (
                          <a
                            href={`https://admin.shopify.com/store/lamptwist-live/orders/${alert.order_id.replace('gid://shopify/Order/', '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded p-1 hover:bg-muted text-muted-foreground hover:text-foreground"
                            title="View in Shopify"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                        <button
                          className={`rounded p-1 ${alert.slack_sent ? 'text-emerald-500 hover:bg-emerald-100 dark:hover:bg-emerald-900/30' : 'text-violet-500 hover:bg-violet-100 dark:hover:bg-violet-900/30'}`}
                          onClick={() => handlePushToSlack(alert)}
                          disabled={pushingSlack === alert.id}
                          title={alert.slack_sent ? 'Sent to Slack — click to resend' : 'Push to Slack'}
                        >
                          {pushingSlack === alert.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                        </button>
                        <button
                          className="rounded p-1 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600"
                          onClick={() => handleDeleteRecord(alert.id)}
                          disabled={deletingRecord === alert.id}
                          title="Delete record"
                        >
                          {deletingRecord === alert.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center text-sm text-muted-foreground">
            No alerts yet. Alerts appear when shipping cost variance exceeds the threshold.
          </div>
        )}
      </div>

      {/* Country carrier-breakdown modal */}
      {expandedCountry && (() => {
        const cs = country_stats.find(c => c.shipping_country === expandedCountry);
        if (!cs) return null;
        const breakdown = cs.carrier_breakdown || [];
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={() => setExpandedCountry(null)}
          >
            <div
              className="bg-card border border-border rounded-xl shadow-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-5 border-b border-border">
                <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                  <span className="text-xl">{countryFlag(cs.shipping_country)}</span>
                  {cs.shipping_country}
                  {isShippyCountry(cs.shipping_country) && <ShippyBadge />}
                  <span className="text-xs text-muted-foreground font-normal ml-2">
                    {cs.count.toLocaleString()} orders &middot; {timeRange !== '0' ? formatRangeLabel(timeRange) : 'all time'}
                  </span>
                </h3>
                <button onClick={() => setExpandedCountry(null)} className="rounded-md p-1 hover:bg-muted text-muted-foreground">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="p-5 overflow-y-auto space-y-5">
                {/* Country summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div className="rounded-md bg-muted/30 p-2.5">
                    <div className="text-muted-foreground">Avg charged</div>
                    <div className="font-mono font-semibold text-foreground">€{fmt(cs.avg_charged)}</div>
                  </div>
                  <div className="rounded-md bg-muted/30 p-2.5">
                    <div className="text-muted-foreground">Avg optimal</div>
                    <div className="font-mono font-semibold text-foreground">€{fmt(cs.avg_optimal)}</div>
                  </div>
                  <div className="rounded-md bg-muted/30 p-2.5">
                    <div className="text-muted-foreground">Avg margin</div>
                    <div className={`font-mono font-semibold ${cs.avg_margin_pct == null ? '' : cs.avg_margin_pct >= 25 ? 'text-emerald-600' : cs.avg_margin_pct >= 20 ? 'text-amber-600' : 'text-red-600'}`}>
                      {cs.avg_margin_pct == null ? '--' : `${Math.round(cs.avg_margin_pct)}%`}
                    </div>
                  </div>
                  <div className="rounded-md bg-muted/30 p-2.5">
                    <div className="text-muted-foreground">Avg volume / order</div>
                    <div className="font-mono font-semibold text-foreground">
                      {cs.avg_volume_cm3 ? `${(cs.avg_volume_cm3 / 1000).toFixed(1)} L` : '--'}
                    </div>
                  </div>
                </div>

                {/* Carrier breakdown */}
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-foreground">Carrier breakdown</h4>
                  {breakdown.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No carrier data for this country in the selected period.</p>
                  ) : (
                    <div className="overflow-x-auto rounded-md border border-border">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-muted/40 text-muted-foreground">
                            <th className="px-2 py-1.5 text-left font-medium">Carrier</th>
                            <th className="px-2 py-1.5 text-right font-medium">Won</th>
                            <th className="px-2 py-1.5 text-right font-medium">Share</th>
                            <th className="px-2 py-1.5 text-right font-medium">Avg cost</th>
                            <th className="px-2 py-1.5 text-right font-medium">Avg volume</th>
                            <th className="px-2 py-1.5 text-right font-medium">Avg bins</th>
                            <th className="px-2 py-1.5 text-right font-medium">Avg items</th>
                            <th className="px-2 py-1.5 text-left font-medium">Top boxes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {breakdown.map((row, i) => (
                            <tr key={row.carrier_name + i} className="border-t border-border">
                              <td className="px-2 py-1.5 font-medium text-foreground">{row.carrier_name}</td>
                              <td className="px-2 py-1.5 text-right font-mono">{row.count.toLocaleString()}</td>
                              <td className="px-2 py-1.5 text-right">
                                <span className="inline-flex items-center gap-1.5">
                                  <span className="font-mono">{row.share_pct.toFixed(1)}%</span>
                                  <span className="inline-block h-1.5 w-12 rounded-full bg-muted overflow-hidden">
                                    <span className="block h-full bg-primary" style={{ width: `${Math.min(100, row.share_pct)}%` }}></span>
                                  </span>
                                </span>
                              </td>
                              <td className="px-2 py-1.5 text-right font-mono">€{row.avg_shipping_cost.toFixed(2)}</td>
                              <td className="px-2 py-1.5 text-right font-mono">
                                {row.avg_volume_cm3 ? `${(row.avg_volume_cm3 / 1000).toFixed(1)} L` : '--'}
                              </td>
                              <td className="px-2 py-1.5 text-right font-mono">{row.avg_bins.toFixed(2)}</td>
                              <td className="px-2 py-1.5 text-right font-mono">{row.avg_items.toFixed(1)}</td>
                              <td className="px-2 py-1.5">
                                {(row.top_bins || []).length === 0 ? (
                                  <span className="text-muted-foreground">--</span>
                                ) : (
                                  <span className="flex flex-wrap gap-1">
                                    {row.top_bins.map((b) => (
                                      <span key={b.name} className="inline-flex items-center rounded-md border border-border bg-background px-1.5 py-0.5 text-[10px] font-mono">
                                        {b.name}<span className="ml-1 text-muted-foreground">×{b.count}</span>
                                      </span>
                                    ))}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Source: <span className="font-mono">PackingAnalysisRecord</span> in the selected period. Carrier name is the winning carrier the order was attributed to.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Dashboard;
